/*
 * 粗品工房 共通機能: 加工方法（のし名入）入力 と 数量ルール
 *  - 商品ページ: 加工方法バリエーションに応じて のし上・のし下・字体 を表示し、
 *    ラインアイテムプロパティ（properties[...]）として送信する。
 *  - 商品ページ・カート: 小ロットの最低数量・追加注文単位を検査し、満たさない場合は
 *    カート追加・決済ボタンを止める。
 * 設定値は商品メタフィールド（namespace: soshina）から Liquid が埋め込む。
 * 未設定・未確認の加工は「受付不可」として扱い、加工可能・無料とは表示しない。
 */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else { root.SoshinaProcessing = api; api.boot(root.document); }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  const NONE = 'なし';
  const NEEDS_WRAP = '包装＋のし名入';

  // ---------- 純粋関数（テスト対象） ----------

  function toInt(v, fallback) {
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  }

  /** 最低数量・追加単位の検査対象か。small_lot（小ロット販売）と min_order（最低注文数量付きの通常商品）。 */
  function hasQtyRule(mode) {
    return mode === 'small_lot' || mode === 'min_order';
  }

  /** 販売区分ごとの数量ルール。通常は「数量1＝1ロット」、小ロット・最低注文数量商品は「数量1＝1販売単位」。 */
  function normalizeRule(raw) {
    const mode = hasQtyRule(raw.mode) ? raw.mode : 'lot';
    return {
      mode,
      lotSize: mode === 'lot' ? toInt(raw.lotSize, 1) : 1,
      unit: raw.unit || '個',
      minQty: hasQtyRule(mode) ? toInt(raw.minQty, 0) : 1,
      step: hasQtyRule(mode) ? toInt(raw.step, 0) : 1,
    };
  }

  /** 数量の検査。検査対象で最低数量・追加単位が未設定なら販売不可として扱う。 */
  function validateQty(rule, qty) {
    const q = Number(qty);
    if (!Number.isInteger(q) || q < 1) return { ok: false, message: '数量を1以上の整数で入力してください。' };
    if (!hasQtyRule(rule.mode)) return { ok: true };
    if (!rule.minQty || !rule.step) {
      return { ok: false, message: 'この商品の最低数量が未設定のため、現在ご注文いただけません。' };
    }
    if (q < rule.minQty) {
      return { ok: false, message: `最低${rule.minQty}${rule.unit}からご注文いただけます。`, suggest: rule.minQty };
    }
    if ((q - rule.minQty) % rule.step !== 0) {
      const up = rule.minQty + Math.ceil((q - rule.minQty) / rule.step) * rule.step;
      const down = up - rule.step;
      return { ok: false, message: `${rule.minQty}${rule.unit}以上、${rule.step}${rule.unit}単位でご注文ください（ご注文できる数量：${down}${rule.unit}または${up}${rule.unit}）。`, suggest: up, suggestDown: down };
    }
    return { ok: true };
  }

  /** ＋ボタン: 次の注文可能数量（最低数量未満・単位外の値からは、その上の注文可能数量へ）。 */
  function stepUp(rule, qty) {
    const q = Number(qty) || 0;
    if (q < rule.minQty) return rule.minQty;
    return rule.minQty + (Math.floor((q - rule.minQty) / rule.step) + 1) * rule.step;
  }

  /** −ボタン: 前の注文可能数量。最低数量より下げない（削除はカートの「削除」で行う）。 */
  function stepDown(rule, qty) {
    const q = Number(qty) || 0;
    if (q <= rule.minQty) return rule.minQty;
    return rule.minQty + (Math.ceil((q - rule.minQty) / rule.step) - 1) * rule.step;
  }

  /** 加工対象数＝課金数量。通常は 数量×ロット入数、小ロットは 数量。 */
  function processingCount(rule, qty) {
    return Number(qty) * rule.lotSize;
  }

  /** 選択中の加工方法が受付可能か。可否が available 以外（未確認・不可・未設定）は受付不可。 */
  function processState(cfg, processName) {
    const name = processName || NONE;
    if (name === NONE) return { process: name, allowed: true, needsInput: false };
    const noshiOk = cfg.noshiStatus === 'available';
    const wrapOk = name !== NEEDS_WRAP || cfg.wrapStatus === 'available';
    const limitsOk = toInt(cfg.upperMax, 0) > 0 && toInt(cfg.lowerMax, 0) > 0 && Array.isArray(cfg.fonts) && cfg.fonts.length > 0;
    if (!noshiOk || !wrapOk || !limitsOk) {
      return { process: name, allowed: false, needsInput: false,
        message: 'この加工方法は対応可否・料金を確認中のため、現在お申し込みいただけません。「なし」を選ぶか、お問い合わせください。' };
    }
    return { process: name, allowed: true, needsInput: true };
  }

  /** のし入力の検査。 */
  function validateNoshi(cfg, values) {
    const errors = [];
    const upper = (values.upper || '').trim();
    const lower = (values.lower || '').trim();
    if (!upper) errors.push('のし上（表書き）を入力してください。');
    if ([...upper].length > toInt(cfg.upperMax, 0)) errors.push(`のし上は${cfg.upperMax}文字以内で入力してください。`);
    if (!lower) errors.push('のし下（お名前・社名）を入力してください。');
    if ([...lower].length > toInt(cfg.lowerMax, 0)) errors.push(`のし下は${cfg.lowerMax}文字以内で入力してください。`);
    if (!cfg.fonts.includes(values.font)) errors.push('字体を選択してください。');
    return errors;
  }

  /** カート行の検査（rules: key → rule, quantities: key → qty）。 */
  function cartViolations(lines) {
    return lines
      .map((l) => ({ ...l, result: validateQty(normalizeRule(l.rule), l.quantity) }))
      .filter((l) => !l.result.ok)
      .map((l) => ({ key: l.key, title: l.title, message: l.result.message }));
  }

  // ---------- 画面処理 ----------

  function readJson(el) {
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function initProduct(doc, box) {
    const cfg = readJson(box.querySelector('[data-soshina-config]'));
    if (!cfg) return;
    const rule = normalizeRule(cfg);
    const formId = box.dataset.formId;
    const form = doc.getElementById(formId);
    if (!form) return;
    const qtyInput = doc.querySelector(`[name="quantity"][form="${formId}"]`) || form.querySelector('[name="quantity"]');
    const fields = box.querySelector('[data-soshina-fields]');
    const notice = box.querySelector('[data-soshina-notice]');
    const countEl = box.querySelector('[data-soshina-count]');
    const inputs = fields ? Array.from(fields.querySelectorAll('input, select')) : [];
    const variantProcess = new Map(cfg.variants.map((v) => [String(v.id), v.process || NONE]));

    // min 属性は付けない（Impulse は change 時に min 未満を黙って min へ直すため、手入力を無断で変えないように）。
    // 初期表示だけ最低数量にする（お客様の入力値ではない）。
    if (qtyInput && hasQtyRule(rule.mode) && rule.minQty && rule.step) {
      if (Number(qtyInput.value) < rule.minQty) qtyInput.value = rule.minQty;
    }
    box.soshinaRule = rule;

    // Impulse は variant:change を送ってから hidden の select[name=id] を更新するため、
    // イベントで受け取った variant を優先し、なければ select の値を読む。
    let eventVariantId = null;
    function currentProcess() {
      const idEl = form.querySelector('[name="id"]');
      const id = eventVariantId || (idEl && idEl.value);
      return variantProcess.get(String(id)) || NONE;
    }

    function setNotice(msgs) {
      notice.textContent = msgs.join(' ');
      notice.hidden = msgs.length === 0;
    }

    function refresh() {
      const st = processState(cfg, currentProcess());
      const show = st.allowed && st.needsInput;
      if (fields) {
        fields.hidden = !show;
        inputs.forEach((i) => { i.disabled = !show; }); // disabled の項目はプロパティとして送信されない
      }
      const qty = qtyInput ? qtyInput.value : 1;
      if (countEl) countEl.textContent = st.process === NONE ? '' : `加工対象数：${processingCount(rule, qty)}${rule.unit}（ご注文数量×${rule.lotSize}${rule.unit}）`;
      const msgs = [];
      if (!st.allowed) msgs.push(st.message);
      const q = validateQty(rule, qty);
      if (!q.ok) msgs.push(q.message);
      setNotice(msgs);
      return { st, q };
    }

    function guard(ev) {
      const { st, q } = refresh();
      const errors = [];
      if (!st.allowed) errors.push(st.message);
      if (!q.ok) errors.push(q.message);
      if (st.allowed && st.needsInput) {
        errors.push(...validateNoshi(cfg, {
          upper: box.querySelector('[data-soshina-upper]').value,
          lower: box.querySelector('[data-soshina-lower]').value,
          font: box.querySelector('[data-soshina-font]').value,
        }));
      }
      if (errors.length) {
        ev.preventDefault(); ev.stopImmediatePropagation();
        setNotice(errors);
        notice.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }

    // テーマ側の送信処理より先に検査する（capture）。
    doc.addEventListener('submit', (ev) => { if (ev.target === form) guard(ev); }, true);
    doc.addEventListener('click', (ev) => {
      const btn = ev.target.closest && ev.target.closest('[data-add-to-cart], button[name="add"]');
      if (btn && (btn.form === form || btn.getAttribute('form') === formId)) guard(ev);
    }, true);
    ['change', 'input'].forEach((t) => doc.addEventListener(t, () => setTimeout(refresh, 0)));
    doc.addEventListener('variant:change', (ev) => {
      const v = ev.detail && ev.detail.variant;
      // 同じページの別商品（関連商品のクイックビュー等）のイベントは無視する
      if (v && variantProcess.has(String(v.id))) eventVariantId = String(v.id);
      else if (v === null || v === undefined) eventVariantId = null;
      setTimeout(refresh, 0);
    });
    refresh();
  }

  function cartLines(doc) {
    return Array.from(doc.querySelectorAll('[data-soshina-cart-rule]')).map((el) => {
      // 行要素（cart-item.liquid の .cart__item[data-key]）。ルール要素自身も data-key を持つため親から探す。
      const item = (el.parentElement && el.parentElement.closest('.cart__item, [data-key]')) || el.parentElement;
      const input = item && item.querySelector('input[name="updates[]"], .js-qty__num');
      return {
        key: el.dataset.key, title: el.dataset.title,
        quantity: input ? input.value : el.dataset.quantity,
        rule: { mode: el.dataset.mode, lotSize: el.dataset.lot, unit: el.dataset.unit, minQty: el.dataset.min, step: el.dataset.step },
      };
    });
  }

  function applyCart(doc) {
    const violations = cartViolations(cartLines(doc));
    const blocked = violations.length > 0;
    // 自分で止めたボタンだけを戻す（テーマや他の処理が止めたボタンを解除しない）
    doc.querySelectorAll('button[name="checkout"], .cart__checkout, [data-soshina-checkout]').forEach((b) => {
      if (blocked && !b.disabled) { b.disabled = true; b.dataset.soshinaDisabled = '1'; }
      else if (!blocked && b.dataset.soshinaDisabled === '1') { b.disabled = false; delete b.dataset.soshinaDisabled; }
    });
    // Shop Pay 等の追加決済ボタンは検査を通らないため、違反時は隠す。
    doc.querySelectorAll('.additional-checkout-buttons, .dynamic-checkout__content, shopify-accelerated-checkout-cart').forEach((b) => {
      if (blocked && !b.hidden) { b.hidden = true; b.dataset.soshinaHidden = '1'; }
      else if (!blocked && b.dataset.soshinaHidden === '1') { b.hidden = false; delete b.dataset.soshinaHidden; }
    });
    // 表示が変わる時だけDOMを書き換える（MutationObserver の再実行で無限ループしないように）
    const message = violations.map((v) => `${v.title}：${v.message}`).join(' ');
    doc.querySelectorAll('.cart__checkout-wrapper, [data-soshina-checkout-wrap]').forEach((wrap) => {
      let box = wrap.querySelector('[data-soshina-cart-alert]');
      if (!blocked) { if (box) box.remove(); return; }
      if (!box) {
        box = doc.createElement('div');
        box.setAttribute('data-soshina-cart-alert', '');
        box.className = 'soshina-cart-alert';
        box.setAttribute('role', 'alert');
        wrap.prepend(box);
      }
      if (box.textContent !== message) box.textContent = message;
    });
    return violations;
  }

  function initCart(doc, fetchFn) {
    const run = () => applyCart(doc);
    ['input', 'change'].forEach((t) => doc.addEventListener(t, (ev) => {
      if (ev.target.matches && ev.target.matches('input[name="updates[]"], .js-qty__num')) setTimeout(run, 0);
    }));
    doc.addEventListener('click', (ev) => { if (ev.target.closest && ev.target.closest('.js-qty__adjust')) setTimeout(run, 50); });
    // カートドロワー・Ajax 再描画に追従
    new MutationObserver(() => run()).observe(doc.body, { childList: true, subtree: true });
    // 決済直前にサーバー側のカート数量で再検査（画面の数量と食い違う場合に備える）
    doc.addEventListener('click', async (ev) => {
      const btn = ev.target.closest && ev.target.closest('button[name="checkout"], .cart__checkout');
      if (!btn || btn.dataset.soshinaVerified === '1') return;
      const rules = new Map(cartLines(doc).map((l) => [l.key, l]));
      if (rules.size === 0) return;
      ev.preventDefault(); ev.stopImmediatePropagation();
      try {
        const cart = await (await fetchFn('/cart.js', { headers: { Accept: 'application/json' } })).json();
        const lines = cart.items.filter((i) => rules.has(i.key)).map((i) => ({ ...rules.get(i.key), quantity: i.quantity }));
        if (cartViolations(lines).length === 0 && applyCart(doc).length === 0) {
          btn.dataset.soshinaVerified = '1'; btn.click(); delete btn.dataset.soshinaVerified;
        } else { applyCart(doc); }
      } catch (e) {
        applyCart(doc);
      }
    }, true);
    run();
  }

  /** ＋/−ボタンの行に対応する小ロットのルールを探す。対象外（通常・未設定）は null＝テーマ本来の動作。 */
  function ruleForQtyWrapper(doc, wrapper) {
    const input = wrapper.querySelector('.js-qty__num, input[name="quantity"], input[name="updates[]"]');
    if (!input) return null;
    let rule = null; let cart = null;
    const item = wrapper.closest('.cart__item');
    if (item) {
      const el = item.querySelector('[data-soshina-cart-rule]');
      if (el) rule = normalizeRule({ mode: el.dataset.mode, lotSize: el.dataset.lot, unit: el.dataset.unit, minQty: el.dataset.min, step: el.dataset.step });
      const form = wrapper.closest('form[data-location]');
      cart = { key: item.dataset.key, namespace: form ? '.cart-' + form.dataset.location : null };
    } else if (input.getAttribute('form')) {
      const box = doc.querySelector(`[data-soshina-product][data-form-id="${input.getAttribute('form')}"]`);
      rule = box && box.soshinaRule;
    }
    if (!rule || !hasQtyRule(rule.mode) || !rule.minQty || !rule.step) return null;
    return { rule, input, cart };
  }

  function initQtyButtons(doc) {
    // Impulse の QtySelector は＋/−で±1するため、小ロット行だけ先に受けて最低数量・追加単位どおりに動かす。
    // カート行はテーマ本来の cart:quantity イベントへ渡し、数量更新・Ajax 再描画はテーマに任せる。
    doc.addEventListener('click', (ev) => {
      const btn = ev.target.closest && ev.target.closest('.js-qty__adjust');
      if (!btn) return;
      const wrapper = btn.closest('.js-qty__wrapper');
      const hit = wrapper && ruleForQtyWrapper(doc, wrapper);
      if (!hit) return;
      ev.preventDefault(); ev.stopImmediatePropagation();
      const current = Number(hit.input.value) || 0;
      const next = btn.classList.contains('js-qty__adjust--plus') ? stepUp(hit.rule, current) : stepDown(hit.rule, current);
      if (next === current) return;
      hit.input.value = next;
      if (hit.cart && hit.cart.namespace) {
        doc.dispatchEvent(new CustomEvent('cart:quantity' + hit.cart.namespace, { detail: [hit.cart.key, next, wrapper] }));
      } else {
        hit.input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }, true);
  }

  function boot(doc) {
    if (!doc) return;
    const start = () => {
      initQtyButtons(doc);
      doc.querySelectorAll('[data-soshina-product]').forEach((box) => initProduct(doc, box));
      if (doc.querySelector('[data-soshina-cart-rule]') || doc.querySelector('.cart__checkout')) initCart(doc, (u, o) => fetch(u, o));
    };
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', start); else start();
  }

  return { normalizeRule, validateQty, stepUp, stepDown, processingCount, processState, validateNoshi, cartViolations, boot };
});
